import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sales } from "../lib/api";
import { fmtDateTime } from "../lib/auditFormat";
import { ReleaseTransitButton } from "./ReleaseTransitButton";
import {
  Truck, ArrowRight, MapPin, Phone, User as UserIcon, Bike, Clock,
  AlertTriangle,
} from "lucide-react";

/** Format age from an ISO datetime string as "Xd Yh" / "Yh Zm" / "Zm". */
function fmtSince(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "0m";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

/**
 * Shared "Transit Hold" queue view. Loads all sales where `transit_hold.active`
 * is true for the currently active location(s) and renders each as a card row.
 *
 * Props:
 *  - locParam:     comma-separated location IDs (matches the active workspace)
 *  - locMap:       { [locationId]: location } — used to render location code
 *  - userMap:      { [userId]: user }         — used to render DSE name
 *  - canRelease:   when true, show the ReleaseTransitButton per row (Accounts)
 *  - releaseUserId: acting user id for audit trail (Accounts users)
 *  - onRefresh:    invoked after a successful release so the parent can refresh
 *  - testidPrefix: role-specific test id prefix (accounts / pdi / bo / spares)
 *  - onCountChange:optional callback with the number of rows (used for tab badges)
 */
export function TransitHoldList({
  locParam,
  locMap = {},
  userMap = {},
  canRelease = false,
  releaseUserId,
  onRefresh,
  testidPrefix = "transit",
  onCountChange,
}) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const sales = await Sales.list({
          transit_only: true,
          location_ids: locParam || undefined,
        });
        if (cancelled) return;
        // Sort: longest on-hold first (oldest engaged_at)
        const sorted = [...sales].sort((a, b) => {
          const aAt = a.transit_hold?.engaged_at || a.updated_at || "";
          const bAt = b.transit_hold?.engaged_at || b.updated_at || "";
          return aAt.localeCompare(bAt);
        });
        setRows(sorted);
        onCountChange?.(sorted.length);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Intentionally omit `onCountChange` from deps — parents often pass an
    // inline callback whose identity changes on every render, which would
    // create an infinite fetch loop. The latest reference is captured via
    // closure on each render.
  }, [locParam, tick]);

  const refresh = () => { setTick((t) => t + 1); onRefresh?.(); };

  if (loading) {
    return (
      <div
        className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500"
        data-testid={`${testidPrefix}-transit-loading`}
      >
        Loading transit vehicles…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center"
        data-testid={`${testidPrefix}-transit-empty`}
      >
        <Truck className="mx-auto mb-3 h-8 w-8 text-slate-300" />
        <div className="text-sm font-semibold text-slate-700">No vehicles on Transit Hold</div>
        <div className="mt-1 text-xs text-slate-500">
          Sales paused for in-transit arrival will appear here.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-transit-list`}>
      {rows.map((sale) => (
        <TransitHoldRow
          key={sale.id}
          sale={sale}
          loc={locMap[sale.location_id]}
          dse={userMap[sale.dse_id]}
          canRelease={canRelease}
          releaseUserId={releaseUserId}
          onViewSale={() => navigate(`/sales/${sale.id}`)}
          onOpenAccounts={() => navigate(`/accounts/${sale.id}`)}
          onReleased={refresh}
          testidPrefix={testidPrefix}
        />
      ))}
    </div>
  );
}

function TransitHoldRow({
  sale, loc, dse, canRelease, releaseUserId,
  onViewSale, onOpenAccounts, onReleased, testidPrefix,
}) {
  const hold = sale.transit_hold || {};
  const vinPresent = !!((sale.vehicle || {}).vin || "").trim();
  const engagedAt = hold.engaged_at;
  const since = fmtSince(engagedAt);
  return (
    <div
      className="group relative rounded-xl border border-blue-200 bg-blue-50/40 p-4 transition-shadow hover:shadow-md"
      data-testid={`${testidPrefix}-transit-row-${sale.id}`}
    >
      <div className="grid grid-cols-1 items-center gap-4 lg:grid-cols-[180px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(180px,1fr)_auto]">
        {/* SR + Engaged since */}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-800">
              <Truck className="h-3 w-3" /> Transit Hold
            </span>
          </div>
          <div className="mt-1.5 font-mono-data text-sm font-bold text-slate-900">
            {sale.sr_number || "—"}
          </div>
          <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-slate-500">
            <Clock className="h-3 w-3" /> On hold {since}
          </div>
        </div>

        {/* Customer */}
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-900">
            {sale.customer?.name || "—"}
          </div>
          <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-600">
            <Phone className="h-3 w-3 text-slate-400" />
            <span className="font-mono-data">{sale.customer?.phone || "—"}</span>
          </div>
        </div>

        {/* Vehicle */}
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            <Bike className="h-3.5 w-3.5 text-slate-400" />
            <span className="truncate">{sale.vehicle?.model || "—"}</span>
          </div>
          {sale.vehicle?.vin ? (
            <div className="mt-1 truncate font-mono-data text-[11px] text-slate-500">
              VIN: {sale.vehicle.vin}
            </div>
          ) : (
            <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700">
              <AlertTriangle className="h-3 w-3" /> VIN pending
            </div>
          )}
        </div>

        {/* Location / DSE */}
        <div className="min-w-0 space-y-1">
          {loc && (
            <div className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-0.5 text-[11px] text-slate-700 ring-1 ring-slate-200">
              <MapPin className="h-3 w-3 text-slate-400" />
              {loc.code}
            </div>
          )}
          {dse && (
            <div className="inline-flex items-center gap-1 text-[11px] text-slate-600">
              <UserIcon className="h-3 w-3 text-slate-400" />
              <span className="truncate">DSE: {dse.name}</span>
            </div>
          )}
        </div>

        {/* Engaged metadata */}
        <div className="min-w-0 space-y-0.5 text-[11px] text-slate-600">
          {engagedAt && (
            <div className="whitespace-nowrap">
              <span className="font-semibold text-slate-500">Since: </span>
              <span className="font-mono-data">{fmtDateTime(engagedAt)}</span>
            </div>
          )}
          {hold.notes && (
            <div className="line-clamp-2 text-slate-500">
              <span className="font-semibold">Notes:</span> {hold.notes}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onViewSale}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            data-testid={`${testidPrefix}-transit-view-${sale.id}`}
          >
            View Sale <ArrowRight className="h-3 w-3" />
          </button>
          {canRelease ? (
            <ReleaseTransitButton
              saleId={sale.id}
              userId={releaseUserId}
              vinPresent={vinPresent}
              variant="compact"
              onDone={onReleased}
            />
          ) : (
            <button
              type="button"
              onClick={onOpenAccounts}
              className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50"
              data-testid={`${testidPrefix}-transit-open-accounts-${sale.id}`}
              title="Only Accounts can release the hold"
            >
              Open in Accounts <ArrowRight className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default TransitHoldList;
